import { isStoreUrl, openStore } from '../../lib/storeLinks.js';
import { DEPARTURE_CARD_ART, type AssistantActivity, type AssistantCard } from '@secretario/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'wouter';
import { cancelReminder, deleteAppointment } from '../../lib/api/personal.js';
import { leaveOutreach } from '../../lib/api/outreach.js';
import { refreshStageData } from '../../lib/stage/refresh.js';
import { skillObject } from '../../lib/areas.js';
import { PersonAvatar } from '../chrome/PersonAvatar.js';
import { isNativeShell } from '../../lib/platform.js';
import { track } from '../../lib/track.js';
import './result-cards.css';

export const ART: Record<AssistantCard['kind'], string> = {
  agenda: '/digest-icons/agenda-compromisso.webp',
  weather: skillObject('clima'),
  day: skillObject('bom_dia'),
  sources: '/digest-icons/topico-curtido.webp',
};

const MORE: Record<AssistantCard['kind'], string> = {
  agenda: 'Abrir a agenda',
  weather: 'Ver a previsão',
  day: 'Abrir a tela inicial',
  sources: '',
};

/** Fonte de pesquisa: abre fora do app (navegador do aparelho). */
function openSource(event: React.MouseEvent, url: string) {
  event.preventDefault();
  window.open(url, '_system', 'noopener,noreferrer');
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/**
 * Desfazer o que o cartão acabou de criar: o item vai para a lixeira da Agenda
 * (volta em até 30 dias), então o toque é seguro.
 */
function UndoButton({ undo }: { undo: NonNullable<AssistantCard['undo']> }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'gone'>('idle');
  async function run() {
    setState('busy');
    try {
      if (undo.kind === 'reminder') await cancelReminder(undo.id);
      else await deleteAppointment(undo.id);
      setState('done');
    } catch {
      // Já tinha sido apagado (por aqui ou pela Agenda): nada a desfazer.
      setState('gone');
    }
    // A agenda, os painéis e o alarme agendado no aparelho acompanham.
    refreshStageData(queryClient);
    void queryClient.invalidateQueries({ queryKey: ['reminders'] });
    void queryClient.invalidateQueries({ queryKey: ['agenda-appointments'] });
  }
  if (state === 'done' || state === 'gone') {
    return (
      <span className="rc-undone" role="status">
        {state === 'done' ? 'Desfeito. Está na lixeira da Agenda.' : 'Já não está na agenda.'}
      </span>
    );
  }
  return (
    <button className="rc-undo" onClick={() => void run()} disabled={state === 'busy'}>
      {state === 'busy' ? 'Desfazendo…' : 'Desfazer'}
    </button>
  );
}

/** Cartão de saída (rota, corrida, ligação): links de `sources` com a arte de passagens. */
export function isDeparture(card: AssistantCard): boolean {
  return card.kind === 'sources' && card.art === DEPARTURE_CARD_ART;
}

/** Qual app o link abre, para o ícone, a legenda e a medição do piloto. */
function departureApp(url: string): { app: string; hint: string } {
  if (url.startsWith('tel:')) return { app: 'telefone', hint: 'Ligar agora' };
  if (url.startsWith('https://wa.me/')) return { app: 'whatsapp', hint: 'Abrir no WhatsApp' };
  if (url.includes('waze.com')) return { app: 'waze', hint: 'Rota com trânsito' };
  if (url.includes('uber.com')) return { app: 'uber', hint: 'Pedir a corrida' };
  return { app: 'maps', hint: 'Rota no mapa' };
}

function DepartureIcon({ app }: { app: string }) {
  if (app === 'telefone') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M6.6 3.5 9 3a1.2 1.2 0 0 1 1.3.7l1.1 2.7a1.2 1.2 0 0 1-.3 1.3l-1.6 1.4a12 12 0 0 0 5.4 5.4l1.4-1.6a1.2 1.2 0 0 1 1.3-.3l2.7 1.1a1.2 1.2 0 0 1 .7 1.3l-.5 2.4a1.6 1.6 0 0 1-1.6 1.3C10.9 20.5 3.5 13.1 3.2 5.1a1.6 1.6 0 0 1 1.3-1.6Z" />
      </svg>
    );
  }
  if (app === 'whatsapp') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4.5 19.5 5.6 16A8 8 0 1 1 8.4 18.6Z" />
        <path d="M9.2 9.1c.3 2 2 3.8 4.4 4.6l1-1.1 1.6.7-.3 1.4c-3.4.2-6.6-2.8-6.9-6.1l1.4-.4.7 1.6Z" />
      </svg>
    );
  }
  if (app === 'uber') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M5 16.5V12l1.7-4.3A2 2 0 0 1 8.6 6.5h6.8a2 2 0 0 1 1.9 1.2L19 12v4.5" />
        <path d="M4 12.2h16M6.5 16.5v1.8M17.5 16.5v1.8" />
        <circle cx="8" cy="14.3" r="1" />
        <circle cx="16" cy="14.3" r="1" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 21s-6-5.4-6-10.5A6 6 0 0 1 18 10.5C18 15.6 12 21 12 21Z" />
      <circle cx="12" cy="10.4" r="2.2" />
    </svg>
  );
}

/**
 * Pronto pra sair: um botão por app (Maps, Waze, Uber ou o telefone), já com o destino.
 * Só abre com o toque do dono; nada sai do aparelho antes disso.
 */
function DepartureCard({ card, index }: { card: AssistantCard; index: number }) {
  function open(url: string, app: string) {
    track('feature_activated', { feature: 'saida', app });
    if (url.startsWith('tel:')) {
      window.location.href = url;
      return;
    }
    window.open(url, isNativeShell() ? '_system' : '_blank', 'noopener');
  }
  return (
    <article className="rc-card departure" style={{ ['--i' as string]: index }}>
      <header>
        <span className="rc-depart-mark" aria-hidden="true">
          <DepartureIcon app={card.rows[0]?.url?.startsWith('tel:') ? 'telefone' : 'maps'} />
        </span>
        <span className="rc-depart-head">
          <small>{card.title}</small>
          {card.value && <strong>{card.value}</strong>}
        </span>
      </header>
      <div className="rc-depart-apps">
        {card.rows
          .filter((row) => row.url)
          .map((row) => {
            const { app, hint } = departureApp(row.url!);
            return (
              <button
                key={row.url}
                type="button"
                className={`rc-depart-app ${app}`}
                onClick={() => open(row.url!, app)}
              >
                <DepartureIcon app={app} />
                <span>
                  <b>{row.text}</b>
                  <em>{hint}</em>
                </span>
              </button>
            );
          })}
      </div>
      <p className="rc-depart-note">Abre só quando você tocar.</p>
    </article>
  );
}

/** Identidade do cartão na lista: o tipo, ou a própria criação quando é uma confirmação. */
export function cardKey(card: AssistantCard): string {
  if (card.undo) return `criado:${card.undo.id}`;
  // Cada habilidade da Equipe tem o seu (contas, lista, datas), mesmo sendo do mesmo tipo.
  return card.art ? `${card.kind}:${card.art}:${card.title}` : card.kind;
}

/** Cartões do que o Secretário consultou: o dado em si, não só a frase. */
export function ResultCards({
  items,
  onOpen,
  onPick,
}: {
  items: AssistantActivity[];
  onOpen?: () => void;
  /** Palco: quem chama abre o painel deste cartão e devolve verdadeiro; senão, segue o link. */
  onPick?: (card: AssistantCard) => boolean;
}) {
  // Um cartão por tipo: se a agenda foi consultada duas vezes, vale a última. A
  // confirmação do que foi criado é outra coisa: cada uma fica, mesmo que a
  // agenda também tenha sido consultada no mesmo pedido.
  const cards = new Map<string, AssistantCard>();
  for (const item of items) {
    if (item.state !== 'done' || !item.card) continue;
    cards.set(cardKey(item.card), item.card);
  }
  if (cards.size === 0) return null;
  // Mensagem de um envio em lote: quem recebeu pode sair dos envios daquele negócio.
  const envio = items.find((item) => item.tool === 'envio' && item.id.startsWith('envio-'));
  return (
    <div className="rc">
      {[...cards.values()].map((card, index) =>
        isDeparture(card) ? (
          <DepartureCard key={cardKey(card)} card={card} index={index} />
        ) : (
          <article
            key={cardKey(card)}
            className={`rc-card ${card.kind}`}
            style={{ ['--i' as string]: index }}
          >
            <header>
              {card.href?.startsWith('/contatos/') ? (
                <PersonAvatar name={card.title} className="rc-person" />
              ) : card.href === '/contatos' ? (
                <PersonAvatar name={null} group className="rc-person" />
              ) : (
                <img src={card.art ? skillObject(card.art) : ART[card.kind]} alt="" />
              )}
              <span>
                <small>{card.title}</small>
                {card.value && <b>{card.value}</b>}
              </span>
            </header>
            <ol>
              {card.rows.map((row, rowIndex) =>
                row.url && isStoreUrl(row.url) ? (
                  <li key={rowIndex} className="store">
                    <button
                      type="button"
                      className="rc-store"
                      onClick={(event) => {
                        event.stopPropagation();
                        void openStore(row.url!);
                      }}
                    >
                      Atualizar agora
                    </button>
                  </li>
                ) : row.url ? (
                  <li key={rowIndex} className="source">
                    <a
                      href={row.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(event) => openSource(event, row.url!)}
                    >
                      <span>{row.text}</span>
                      <em>{hostOf(row.url)}</em>
                    </a>
                  </li>
                ) : (
                  <li key={rowIndex} className={row.tone}>
                    {row.when && <em>{row.when}</em>}
                    <span>{row.text}</span>
                  </li>
                ),
              )}
            </ol>
            <footer className="rc-foot">
              {card.href && (
                <Link
                  href={card.href}
                  className="rc-more"
                  onClick={(event) => {
                    if (onPick?.(card)) event.preventDefault();
                    else onOpen?.();
                  }}
                >
                  {card.more ?? MORE[card.kind]}
                </Link>
              )}
              {card.undo && <UndoButton undo={card.undo} />}
            </footer>
          </article>
        ),
      )}
      {envio && <LeaveOutreach batchId={envio.id.slice(6, 42)} sender={envio.label} />}
    </div>
  );
}

/** "Não quero receber mais deste negócio": um toque, e os envios em lote dele param. */
function LeaveOutreach({ batchId, sender }: { batchId: string; sender: string }) {
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'fail'>('idle');
  if (state === 'done')
    return <p className="rc-leave done">Pronto: você não recebe mais envios de {sender}.</p>;
  return (
    <button
      type="button"
      className="rc-leave"
      disabled={state === 'busy'}
      onClick={() => {
        setState('busy');
        leaveOutreach(batchId)
          .then(() => setState('done'))
          .catch(() => setState('fail'));
      }}
    >
      {state === 'fail'
        ? 'Não consegui agora. Tocar de novo'
        : `Não quero receber mais envios de ${sender}`}
    </button>
  );
}
